import { Outlet, useNavigate, useParams } from "react-router-dom";
import {
  RpcEndpointsUrl,
  RpcEndpontGetStartedUrl,
  RpcEndpontMetricsUrl,
  RpcEndpontSecurityUrl,
} from "../../utils/urls";
import RightArrow from "./../../assets/icons/arrow-right.svg?react";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  getRpcAsync,
  selectLoading,
  selectRpc,
} from "../../store/rpc/rpcSlice";
import { Helmet } from "react-helmet";
import { NotExist, RpcNotActiveTab } from "../../components/layouts/NotData";
import SecurityIcon from "./../../assets/icons/lock-closed.svg?react";
import ExportIcon from "./../../assets/icons/arrow-right-start-on-rectangle.svg?react";
import MetricsIcon from "./../../assets/icons/adjustments-vertical.svg?react";
import { Flex, Skeleton, Typography, TabOptions, Tabs } from "djuno-design";
import { RpcRefreshStatus } from "../../store/rpc/rpcsSlice";

const generateRpcEndpointRoutes = (id: string | undefined): TabOptions => {
  return [
    {
      label: (
        <div className="flex items-center gap-1">
          <ExportIcon className="w-3" />
          Get Started
        </div>
      ),
      url: RpcEndpontGetStartedUrl(id ? +id : 0),
      testId: "1th-tab",
    },

    {
      label: (
        <div className="flex items-center gap-1 metrics">
          <MetricsIcon className="w-3" />
          Metrics
        </div>
      ),
      url: RpcEndpontMetricsUrl(id ? +id : 0),
      testId: "2th-tab",
    },
    {
      label: (
        <div className="flex items-center gap-1 security">
          <SecurityIcon className="w-3" />
          Security
        </div>
      ),
      url: RpcEndpontSecurityUrl(id ? +id : 0),
      testId: "3th-tab",
    },
  ];
};

const RpcEndpointPage = () => {
  const { id } = useParams();
  const rpc = useAppSelector(selectRpc);
  const loading = useAppSelector(selectLoading);
  const EndpontPageMenus = generateRpcEndpointRoutes(id);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isExist, setIsExist] = useState<boolean>(true);

  const isActive = useMemo(() => {
    if (!rpc) return true;
    return rpc.RPCNodeStatus === "RUNNING";
  }, [rpc]);

  useEffect(() => {
    if (id) {
      dispatch(getRpcAsync({ id: +id })).then((action) => {
        if (action && action.type === "rpc/rejected") {
          setIsExist(false);
        }
      });
    }
  }, [dispatch, id]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    const intervalTime =
      Number(import.meta.env.VITE_NODES_REFRESH_INTERVAL) || 20000;

    if (rpc && RpcRefreshStatus.includes(rpc.RPCNodeStatus)) {
      interval = setInterval(() => {
        if (id) {
          dispatch(getRpcAsync({ id: +id, withoutLoading: true })).then(
            (action) => {
              if (action && action.type === "rpc/rejected") {
                setIsExist(false);
              }
            }
          );
        }
      }, intervalTime);
    } else {
      if (interval) clearInterval(interval);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [dispatch, id, rpc]);

  return (
    <>
      <Helmet>
        <title>
          {import.meta.env.VITE_APP_NAME} | Endpoints ({rpc ? rpc.RpcName : ""})
        </title>
        <meta
          name="description"
          content="RPC-endpoints, or Remote Procedure Call endpoints, are a fundamental aspect of distributed computing systems, enabling communication between different components or services across a network"
        />
      </Helmet>
      <Flex items="center" justify="between" className="h-16">
        <Flex items="center" justify="between" className="px-6 flex-1">
          <Flex items="center" className="gap-2">
            <RightArrow
              onClick={() => navigate(RpcEndpointsUrl)}
              className="rotate-180 w-5 h-5 hover:scale-110 transition-all duration-500 text-slate-800 dark:text-slate-100 cursor-pointer"
            />
            {rpc && !loading && (
              <>
                <div className="w-10 h-10 rounded-md ring-0 flex items-center text-center justify-center border dark:border-dark-2">
                  <img src={rpc?.Icon} alt={rpc?.RpcName} className="w-6 h-6" />
                </div>
                <div className="flex flex-col">
                  <Typography.Title level={5} className="!mb-0">
                    {rpc.RpcName}
                  </Typography.Title>
                  <Typography.Text uiType="secondary" className="!text-sm">
                    {rpc && `${rpc.ServiceName} (${rpc.NetworkType})`}
                  </Typography.Text>
                </div>
              </>
            )}

            {loading && (
              <>
                <Skeleton shape="square" uiSize="large" />
                <Flex direction="col" className="gap-1">
                  <Skeleton
                    shape="rectangle"
                    style={{ width: 200, height: 20 }}
                  />
                  <Skeleton
                    shape="rectangle"
                    style={{ width: 100, height: 15 }}
                  />
                </Flex>
              </>
            )}
          </Flex>
        </Flex>
      </Flex>

      {isExist && isActive && (
        <div className="px-6 mt-5 mb-10">
          <Tabs
            options={EndpontPageMenus.map((o) => ({
              label: o.label,
              url: o.url,
              testId: o.testId,
            }))}
          />
          <div className="py-6">
            <Outlet />
          </div>
        </div>
      )}
      {!isExist && <NotExist url={() => navigate(RpcEndpointsUrl)} />}
      {!loading && rpc && !isActive && (
        <RpcNotActiveTab nodeStatus={rpc.RPCNodeStatus} />
      )}
    </>
  );
};
export default RpcEndpointPage;
